import type { ParsedRule } from '@/pages/Screener/functions';
import useUser from '@/hooks/useUser';
import { useInfiniteQuery } from '@tanstack/react-query';

import { queryFn, getQueryKey } from './queryFn';
import type { CompanyScreenerResults } from './queryFn';

type GenNextPageParamCreatorParams = {
  limit: number;
  initPage: number;
  totalHits?: number | null;
  allCurrentPagesLength: number;
};

export const calcNextPage = ({
  limit,
  initPage,
  totalHits,
  allCurrentPagesLength,
}: GenNextPageParamCreatorParams) => {
  const totalPages = totalHits ? Math.ceil(totalHits / limit) : 0;
  const nextPage = initPage + allCurrentPagesLength;
  return nextPage < totalPages ? nextPage : null;
};

type HookParams = {
  rules?: ParsedRule[];
  limit: number;
  initPage?: number;
  enabled?: boolean;
  returnRecentCompaniesOnly?: boolean;
};

export const useInfiniteCompanyScreenersQuery = ({
  rules,
  limit,
  initPage = 0,
  enabled = true,
  returnRecentCompaniesOnly,
}: HookParams) => {
  const { data } = useUser();
  const isGuest = !data.loggedIn;
  return useInfiniteQuery({
    enabled: enabled && Boolean(rules?.length && limit),
    queryFn,
    queryKey: getQueryKey({
      rules,
      limit,
      initPage,
      isGuest,
      returnRecentCompaniesOnly,
    }),
    staleTime: 60000 * 60, // 1 hour
    initialPageParam: initPage,
    retry: 1,
    getNextPageParam: (
      lastPage: CompanyScreenerResults,
      allPages: CompanyScreenerResults[]
    ) => {
      const totalHits = lastPage.totalHits;
      const newCompaniesTotalHits = lastPage.newCompaniesTotalHits;
      const allCurrentPagesLength = allPages.length;

      return calcNextPage({
        limit,
        initPage,
        totalHits: returnRecentCompaniesOnly
          ? newCompaniesTotalHits
          : totalHits,
        allCurrentPagesLength,
      });
    },
  });
};
